// ─────────────────────────────────────────────────────────────────────
// Access Management ▸ Role Permissions tab — Round 4 (2026).
//
// Pick a role, then check/uncheck the permissions (modules) it grants.
// Reads/writes via Round-2 AccessRolesController:
//   GET /api/admin/access/roles/:id/permissions  → catalog + assigned flags
//   PUT /api/admin/access/roles/:id/permissions  → replace set (transactional)
//
// Save uses the transactional PUT (server computes the add/remove diff), so
// a partial failure can never leave a role half-updated. The Save button is
// enabled only when the local selection differs from what's on the server
// (dirty-state detection).
//
// Permissions are grouped by category for readability. Category is derived
// from the permission NAME (a presentation-only heuristic — no schema, no
// backend dependency). Anything unmatched falls under "Other".
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import useAuth from "../../Personal Attendance/useAuth";
import { detachRoleGroup } from "../../../api/accessManagement";
import {
    Row, Col, Card, CardBody, CardHeader, Input, Button, Spinner, Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    fetchRoles, fetchRolePermissions, replaceRolePermissions,
} from "../../../api/accessManagement";

// Presentation-only category heuristic, by permission-name prefix/keyword.
// Order matters (first match wins). Purely cosmetic grouping for the picker.
const CATEGORY_RULES = [
    { label: "Dashboards",        test: (n) => /\bDashboard\b/i.test(n) },
    { label: "KPI Reports",       test: (n) => n.startsWith("KPI") },
    { label: "Overtime",          test: (n) => n.startsWith("Overtime") },
    { label: "EPO",               test: (n) => n.startsWith("EPO - ") || /\bEPO\b/i.test(n) || n === "Mark Items as Received" || n === "Find EPO" || n === "Accounting EPO Viewer" || n === "MPC EPO Viewer" },
    { label: "Attendance (FTO/COA)", test: (n) => n.startsWith("Attendance - ") },
    { label: "Travel",            test: (n) => n.startsWith("Travel - ") },
    { label: "Projects",          test: (n) => n.startsWith("Projects - ") || n.startsWith("View Projects") || n.includes("Project") },
    { label: "DMS / Documents",   test: (n) => n.startsWith("DMS") || n.startsWith("DMS - ") || n === "Documents Ready for Release" },
    { label: "Tasks",             test: (n) => n.startsWith("Task Management") || n === "Task Auditor" || n.includes("Task") },
    { label: "Human Resources",   test: (n) => n === "HR Menu" || n.includes("FTO") || n.includes("COA") },
    { label: "Supervisor",        test: (n) => n === "Supervisor Hub" },
    { label: "Finance",           test: (n) => n === "Finance Access" },
    { label: "Operations",        test: (n) => n.includes("Operations") },
    { label: "Documents & Signatory", test: (n) => n === "SN Signatory" },
    { label: "Apps & Modules",    test: (n) => n.endsWith(" - Open") },
    { label: "Support",           test: (n) => n === "Ticket Manager" || n === "Support - Open" },
    { label: "India / Shukla",    test: (n) => n.includes("India") },
    { label: "Administration",    test: (n) => n.startsWith("Admin - ") || n.includes("Permissions Editor") || n === "Super User" || n === "View Beta Features" },
];
const categoryFor = (name) => {
    const n = name || "";
    for (const rule of CATEGORY_RULES) { if (rule.test(n)) return rule.label; }
    return "Other";
};

export default function RolePermissionsTab({ initialRoleId }) {
    const [roles, setRoles]   = useState([]);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [roleId, setRoleId] = useState(initialRoleId ?? null);

    const [detail, setDetail]   = useState(null);  // RolePermissionsDto
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    // Local selection = Set of assigned permission names (the editable state).
    const [selected, setSelected] = useState(() => new Set());
    const [serverSet, setServerSet] = useState(() => new Set()); // baseline for dirty check
    const [search, setSearch]   = useState("");
    const [saving, setSaving]   = useState(false);

    // Load role list once.
    useEffect(() => {
        (async () => {
            setRolesLoading(true);
            try {
                const all = await fetchRoles();
                // Hide the IT Admin role here: it is an unconditional super-user
                // (EffectiveAccessService gates everything with isItAdmin || ...),
                // so its permission checkboxes can never restrict or expand what
                // an IT Admin sees — editing them is meaningless and confusing.
                // To GRANT access you edit OTHER roles (here) or individuals
                // (Direct User Permissions tab). The IT Admin role still exists
                // and is managed (protected) on the Roles tab.
                // Round 1: the system role is flagged by the API (isProtected) —
                // no name matching in the UI.
                const list = all.filter((r) => r.isProtected !== true);
                setRoles(list);
                // Default selection: the role passed in (if it's still in the
                // editable list), else the first editable role.
                const passedOk = initialRoleId && list.some((r) => r.roleId === initialRoleId);
                if (passedOk) setRoleId(initialRoleId);
                else if (list.length && (roleId == null || !list.some((r) => r.roleId === roleId)))
                    setRoleId(list[0].roleId);
            } catch {
                toast.error("Could not load roles.");
            } finally {
                setRolesLoading(false);
            }
        })();
        
    }, []);

    // If the parent changes the focused role (e.g. user clicked "Permissions"
    // on the Roles tab), follow it.
    useEffect(() => {
        if (initialRoleId != null) setRoleId(initialRoleId);
    }, [initialRoleId]);

    // Load the selected role's permission picture.
    useEffect(() => {
        if (roleId == null) { setDetail(null); return; }
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const dto = await fetchRolePermissions(roleId);
                if (cancelled) return;
                if (!dto) { setError("Role not found."); setDetail(null); return; }
                setDetail(dto);
                const assigned = new Set((dto.assignedPermissionNames || []).map((x) => x));
                setSelected(new Set(assigned));
                setServerSet(new Set(assigned));
            } catch {
                if (!cancelled) setError("Could not load this role's permissions.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [roleId]);

    // Group the catalog by category, filtered by search.
    const grouped = useMemo(() => {
        const items = (detail?.permissions || []).filter((p) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            return (p.permissionName || "").toLowerCase().includes(q) ||
                   (p.description    || "").toLowerCase().includes(q);
        });
        const map = new Map();
        for (const p of items) {
            const cat = categoryFor(p.permissionName);
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(p);
        }
        // Stable category order: defined categories first, then Other last.
        const order = [...CATEGORY_RULES.map((r) => r.label), "Other"];
        return [...map.entries()].sort(
            (a, b) => order.indexOf(a[0]) - order.indexOf(b[0])
        );
    }, [detail, search]);

    // Dirty = local selection differs from server baseline.
    const dirty = useMemo(() => {
        if (selected.size !== serverSet.size) return true;
        for (const x of selected) if (!serverSet.has(x)) return true;
        return false;
    }, [selected, serverSet]);

    // ── Round 1: protected permissions & delegation ceiling ────────────
    // The API returns protectedPermissionNames (Admin - *, Super User, Account
    // Security - Administer, …). Only an IT Admin may add or remove those, and
    // a non-IT-Admin may only add permissions they hold themselves (server rule
    // R-C). The checkboxes are locked accordingly; the server enforces the same
    // rules regardless of what the UI shows.
    const auth = useAuth();
    const protectedSet = useMemo(
        () => new Set((detail?.protectedPermissionNames || []).map((n) => n.toLowerCase())),
        [detail]
    );
    const isProtectedPerm = (name) => protectedSet.has(String(name || "").toLowerCase());
    const isLocked = (name) => {
        if (auth.isItAdmin) return false;
        if (isProtectedPerm(name)) return true;                       // R-A
        return !serverSet.has(name) && !auth.hasAccess(name);         // R-C (adding beyond own ceiling)
    };
    const lockReason = (name) =>
        isProtectedPerm(name)
            ? "Protected permission — only an IT Admin can change it."
            : "You can only add permissions you hold yourself.";

    const toggle = (name) => {
        if (isLocked(name)) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const toggleCategory = (perms, allOn) => {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const p of perms) {
                if (isLocked(p.permissionName)) continue;   // never touch locked items
                if (allOn) next.delete(p.permissionName);
                else next.add(p.permissionName);
            }
            return next;
        });
    };

    const resetSelection = () => setSelected(new Set(serverSet));

    // Global "select everything" / "clear everything" — operates on the ENTIRE
    // catalog for this role (every permission, all categories, no exceptions),
    // not just the currently-filtered view. This is what makes a role a true
    // super-set when desired.
    const selectAllPermissions = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const p of (detail?.permissions || []))
                if (!isLocked(p.permissionName)) next.add(p.permissionName);
            return next;
        });
    };
    const clearAllPermissions = () =>
        setSelected((prev) => {
            const next = new Set();
            // keep locked items exactly as the server has them
            for (const x of prev) if (isLocked(x) && serverSet.has(x)) next.add(x);
            return next;
        });

    const onSave = async () => {
        if (!detail) return;
        setSaving(true);
        try {
            const names = [...selected];
            const res = await replaceRolePermissions(detail.roleId, names);
            const added   = res?.added?.length   || 0;
            const removed = res?.removed?.length || 0;
            toast.success(
                added || removed
                    ? `Saved — ${added} added, ${removed} removed.`
                    : "Saved — no changes."
            );
            // Re-baseline from the fresh role picture the server returned.
            const fresh = res?.role;
            if (fresh) {
                setDetail(fresh);
                const assigned = new Set((fresh.assignedPermissionNames || []).map((x) => x));
                setSelected(new Set(assigned));
                setServerSet(new Set(assigned));
            } else {
                setServerSet(new Set(selected));
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || (e?.response?.status === 403 ? "You are not allowed to make that change." : "Could not save — please retry."));
        } finally {
            setSaving(false);
        }
    };

    const selectedRole = roles.find((r) => r.roleId === roleId);

    return (
        <>
            <Row className="g-2 align-items-center mb-3">
                <Col md={5}>
                    <label className="form-label small text-muted mb-1">Role</label>
                    <Input
                        type="select"
                        bsSize="sm"
                        value={roleId ?? ""}
                        onChange={(e) => setRoleId(Number(e.target.value))}
                        disabled={rolesLoading}
                    >
                        {rolesLoading && <option>Loading…</option>}
                        {!rolesLoading && roles.length === 0 && <option value="">No roles</option>}
                        {!rolesLoading && roles.map((r) => (
                            <option key={r.roleId} value={r.roleId}>
                                {r.roleName}{r.isActive ? "" : " (disabled)"}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col md={4}>
                    <label className="form-label small text-muted mb-1">Filter permissions</label>
                    <Input
                        type="search"
                        bsSize="sm"
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col md={3} className="text-md-end">
                    <label className="form-label small text-muted mb-1 d-block">&nbsp;</label>
                    <Button color="light" size="sm" className="me-2" onClick={resetSelection}
                        disabled={!dirty || saving}>
                        Reset
                    </Button>
                    <Button color="primary" size="sm" onClick={onSave} disabled={!dirty || saving || !!detail?.isSharedGroup}
                        title={detail?.isSharedGroup ? "Blocked: this role shares its permission group — detach it first." : undefined}>
                        {saving ? <><Spinner size="sm" /> Saving…</> : "Save changes"}
                    </Button>
                </Col>
            </Row>

            <div className="small text-muted mb-3 d-flex align-items-start gap-2">
                <i className="ri-information-line mt-1" />
                <span>
                    Set the <strong>default</strong> access for each role here. Anyone with the role
                    gets these on their next sign-in; you can remove a specific item from one person
                    in the <strong>Direct User Permissions</strong> tab. The <strong>IT Admin</strong>{" "}
                    role isn't listed — IT Admins always have full access, so there's nothing to set.
                </span>
            </div>

            {/* Round 4: shared write-target warning. The server refuses edits while
                the role's permission group is shared with other roles. */}
            {selectedRole && detail?.isSharedGroup && (
                <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
                    <i className="ri-error-warning-line mt-1" />
                    <div className="flex-grow-1">
                        <strong>{selectedRole.roleName}</strong> shares its permission group
                        {" "}<code>{detail.groupName}</code> with{" "}
                        {(detail.sharedWithRoles || []).map((r) => r.roleName).join(", ")}.
                        Editing it would change those roles too, so saving is blocked.
                        <div className="mt-2">
                            <Button color="warning" size="sm" disabled={saving} onClick={async () => {
                                if (!window.confirm(`Give "${selectedRole.roleName}" its own permission group?\n\nIts current permissions are copied to a dedicated group; the other roles keep the shared group unchanged.`)) return;
                                setSaving(true);
                                try {
                                    const fresh = await detachRoleGroup(selectedRole.roleId);
                                    setDetail(fresh);
                                    const assigned = new Set(fresh.assignedPermissionNames || []);
                                    setSelected(new Set(assigned)); setServerSet(new Set(assigned));
                                    toast.success("The role now owns its permission group.");
                                } catch (e) {
                                    toast.error(e?.response?.data?.error || "Could not detach the group.");
                                } finally { setSaving(false); }
                            }}>
                                <i className="ri-git-branch-line me-1" />Give this role its own group
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {selectedRole && (
                <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
                    <span className="small text-muted">
                        Editing permissions for <strong>{selectedRole.roleName}</strong>
                    </span>
                    {detail && (
                        <span className="small text-muted">
                            · {selected.size}/{(detail.permissions || []).length} selected
                        </span>
                    )}
                    {detail && (
                        <>
                            <Button color="link" size="sm" className="p-0 ms-2 text-decoration-none"
                                onClick={selectAllPermissions} disabled={saving}>
                                <i className="ri-checkbox-multiple-line align-bottom me-1" />
                                Select all permissions
                            </Button>
                            <span className="text-muted">·</span>
                            <Button color="link" size="sm" className="p-0 text-decoration-none text-danger"
                                onClick={clearAllPermissions} disabled={saving}>
                                <i className="ri-checkbox-multiple-blank-line align-bottom me-1" />
                                Clear all
                            </Button>
                        </>
                    )}
                    {dirty && <Badge color="warning" className="ms-1">Unsaved changes</Badge>}
                </div>
            )}

            {loading ? (
                <div className="text-center text-muted py-5"><Spinner size="sm" /> Loading permissions…</div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : !detail ? (
                <div className="text-center text-muted py-5">Pick a role to manage its permissions.</div>
            ) : grouped.length === 0 ? (
                <div className="text-center text-muted py-4 small">No permissions match your search.</div>
            ) : (
                <Row className="g-3">
                    {grouped.map(([category, perms]) => {
                        const allOn = perms.every((p) => selected.has(p.permissionName));
                        const someOn = perms.some((p) => selected.has(p.permissionName));
                        return (
                            <Col lg={6} key={category}>
                                <Card className="border-0 shadow-sm h-100">
                                    <CardHeader className="bg-white border-0 d-flex align-items-center justify-content-between py-2">
                                        <span className="fw-semibold">
                                            {category}
                                            <Badge color="light" className="text-muted ms-2">
                                                {perms.filter((p) => selected.has(p.permissionName)).length}/{perms.length}
                                            </Badge>
                                        </span>
                                        <Button color="link" size="sm" className="p-0 text-decoration-none"
                                            onClick={() => toggleCategory(perms, allOn)}>
                                            {allOn ? "Clear all" : someOn ? "Select all" : "Select all"}
                                        </Button>
                                    </CardHeader>
                                    <CardBody className="pt-1">
                                        {perms.map((p) => (
                                            <div key={p.permissionName} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`perm-${detail.roleId}-${p.permissionName}`}
                                                    checked={selected.has(p.permissionName)}
                                                    disabled={isLocked(p.permissionName)}
                                                    title={isLocked(p.permissionName) ? lockReason(p.permissionName) : undefined}
                                                    onChange={() => toggle(p.permissionName)}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor={`perm-${detail.roleId}-${p.permissionName}`}
                                                    title={isLocked(p.permissionName) ? lockReason(p.permissionName) : undefined}
                                                >
                                                    <span className="fw-medium">{p.permissionName}</span>
                                                    {(detail.unusedPermissionNames || []).some((n) => n.toLowerCase() === p.permissionName.toLowerCase()) && (
                                                        <span className="badge bg-secondary-subtle text-secondary ms-2" title="Kept for a future module (decision D14): no screen or API uses it today.">
                                                            not used by any screen
                                                        </span>
                                                    )}
                                                    {(detail.inertPermissionNames || []).some((n) => n.toLowerCase() === p.permissionName.toLowerCase()) && (
                                                        <span className="badge bg-info-subtle text-info ms-2" title="Apps are open to every active employee (decision D8); this row no longer gates anything.">
                                                            apps are open to all
                                                        </span>
                                                    )}
                                                    {isProtectedPerm(p.permissionName) && (
                                                        <span className="badge bg-danger-subtle text-danger ms-2" title="Protected — only an IT Admin can grant or remove this">
                                                            <i className="ri-lock-2-line" /> protected
                                                        </span>
                                                    )}
                                                    {p.description && (
                                                        <span className="text-muted small d-block">{p.description}</span>
                                                    )}
                                                </label>
                                            </div>
                                        ))}
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
        </>
    );
}
